/**
 * MOBZY Product Page — vanilla JS, zero dependencies.
 * Loaded with `defer` so it never blocks rendering.
 * Uses event delegation (one listener per section) so this scales fine
 * even if the theme renders many product blocks on one page.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * WISHLIST — persisted in localStorage so items survive reloads and
   * can be read by a wishlist listing page. Swap WISHLIST_KEY read/
   * write here for a real backend/app API if one is added later.
   * ------------------------------------------------------------------ */
  var WISHLIST_KEY = 'mobzy_wishlist';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (err) { /* storage unavailable (private mode, quota) — fail silently */ }
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: list }));
  }

  function isWishlisted(handle) {
    return readWishlist().some(function (item) { return item.handle === handle; });
  }

  function addToWishlist(btn) {
    var handle = btn.getAttribute('data-mz-wishlist-handle') || btn.getAttribute('data-handle');
    if (!handle) return;
    var list = readWishlist();
    if (list.some(function (item) { return item.handle === handle; })) return;
    list.push({
      handle: handle,
      productId: btn.getAttribute('data-mz-wishlist-id'),
      variantId: btn.getAttribute('data-mz-wishlist-variant-id'),
      title: btn.getAttribute('data-mz-wishlist-title') || btn.getAttribute('data-title'),
      url: btn.getAttribute('data-mz-wishlist-url') || btn.getAttribute('data-url'),
      image: btn.getAttribute('data-mz-wishlist-image') || btn.getAttribute('data-image'),
      price: btn.getAttribute('data-mz-wishlist-price') || btn.getAttribute('data-price'),
      addedAt: Date.now()
    });
    writeWishlist(list);
  }

  function removeFromWishlist(handle) {
    if (!handle) return;
    var list = readWishlist().filter(function (item) { return item.handle !== handle; });
    writeWishlist(list);
  }

  /* ------------------------------------------------------------------
   * MONEY FORMATTING — mirrors Shopify's standard money_format tokens
   * ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
   * {{amount_no_decimals_with_comma_separator}}) so prices computed on
   * the client match what Liquid's `| money` filter would have printed.
   * Shopify money values are always integers in the smallest currency
   * unit (e.g. cents), same as what `product.variants | json` outputs.
   * ------------------------------------------------------------------ */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = isNaN(precision = Math.abs(precision)) ? 2 : precision;
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;
    var negative = number < 0 ? '-' : '';
    var n = Math.abs(Number(number) || 0).toFixed(precision);
    var parts = n.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return negative + dollars + cents;
  }

  function formatMoney(cents, format) {
    if (cents === null || cents === undefined) return '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var formatStr = format || '${{amount}}';
    var match = formatStr.match(placeholder);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents / 100, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 0, '.', ',');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents / 100, 2);
    }
    return formatStr.replace(placeholder, value);
  }

  function safeParseJSON(str) {
    if (!str) return null;
    try { return JSON.parse(str); } catch (err) { return null; }
  }

  document.querySelectorAll('[data-mz-product]').forEach(initProduct);

  function initProduct(root) {
    const mainImg = root.querySelector('#mz-main-image');
    const swipeArea = root.querySelector('[data-mz-swipe-area]');
    const thumbsStrip = root.querySelector('[data-mz-thumbs]');
    const dotsWrap = root.querySelector('[data-mz-dots]');
    const qtyInput = root.querySelector('[data-mz-qty-input]');
    const stickyCta = root.querySelector('[data-mz-sticky-cta]');
    const viewerCountEl = root.querySelector('[data-mz-viewer-count]');
    const cartMsgEl = root.querySelector('[data-mz-cart-msg]');
    const productForm = root.querySelector('#mz-product-form');
    const addBtn = root.querySelector('[data-mz-add-to-cart]');
    const buyBtnEl = root.querySelector('[data-mz-buy-now]');
    const skuEl = root.querySelector('[data-mz-sku]');
    const priceNowEl = root.querySelector('[data-mz-price-now]');
    const priceWasEl = root.querySelector('[data-mz-price-was]');
    const priceSaveEl = root.querySelector('[data-mz-price-save]');
    const moneyFormat = root.getAttribute('data-mz-money-format') || '${{amount}}';

    // Max units of THIS product allowed in the cart at once — checked
    // against whatever is already in the cart, not just this page load,
    // so repeated add attempts can never push the total past the cap.
    const MAX_PER_PRODUCT = parseInt(root.getAttribute('data-mz-max-per-product'), 10) || 3;

    const wishBtnEl = root.querySelector('[data-mz-wishlist]');
    if (wishBtnEl) {
      const savedHandle = wishBtnEl.getAttribute('data-mz-wishlist-handle') || wishBtnEl.getAttribute('data-handle');
      if (savedHandle && isWishlisted(savedHandle)) {
        wishBtnEl.setAttribute('aria-pressed', 'true');
      }
    }

    /* ------------------------------------------------------------------
     * VARIANT RESOLUTION — the actual fix.
     *
     * Previously, clicking a colour swatch (or changing the gallery
     * image) only updated the *visual* state (active swatch, hero photo)
     * and never touched which variant was actually selected. So the
     * hidden variant-id input used by "Add to Cart" / "Buy It Now" kept
     * pointing at whatever variant the page loaded with — the picture
     * would show orange, but the item added to cart was still black.
     *
     * variants: full list of this product's variants, embedded server-
     *   side as JSON (id, option1/2/3, price, compare_at_price, sku,
     *   available, featured_image).
     * optionNames: the product's option names in order (e.g.
     *   ["Color","Size"]), so option1/2/3 can be matched to the right
     *   option regardless of which swatch/button the shopper touched.
     * selectedOptions: current value picked for each option, in the
     *   same order as optionNames — this is what we resolve against
     *   `variants` to find the one true matching variant.
     * ------------------------------------------------------------------ */
    const variantsDataEl = root.querySelector('[data-mz-variants]');
    const variants = safeParseJSON(variantsDataEl ? variantsDataEl.textContent : null) || [];
    const optionNames = safeParseJSON(root.getAttribute('data-mz-option-names')) || [];

    function getVariantId() {
      const hiddenId = productForm ? productForm.querySelector('[data-mz-variant-id]') : null;
      return hiddenId ? hiddenId.value : null;
    }

    function findVariantById(id) {
      return variants.find(v => String(v.id) === String(id));
    }

    function findVariantByOptions(options) {
      return variants.find(v => {
        return (optionNames.length < 1 || v.option1 === options[0]) &&
               (optionNames.length < 2 || v.option2 === options[1]) &&
               (optionNames.length < 3 || v.option3 === options[2]);
      });
    }

    // Seed selectedOptions from whichever variant the page actually
    // loaded with (the hidden input's initial value), so we start in
    // sync with the server-rendered price/SKU/etc.
    let selectedOptions = (function () {
      const currentVariant = findVariantById(getVariantId()) || variants[0];
      if (!currentVariant) return [];
      return [currentVariant.option1, currentVariant.option2, currentVariant.option3].slice(0, optionNames.length || 3);
    })();

    function setVariantImage(variant) {
      if (!mainImg || !variant || !variant.featured_image || !variant.featured_image.src) return;
      const src = variant.featured_image.src;
      if (mainImg.hasAttribute('srcset')) {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
      }
      mainImg.classList.add('is-swapping');
      window.setTimeout(() => {
        mainImg.src = src;
        mainImg.classList.remove('is-swapping');
      }, 90);
    }

    function applyVariantToUI(variant) {
      if (!variant) return;

      // Hidden input used by the add-to-cart form / cart-cap logic.
      const hiddenId = productForm ? productForm.querySelector('[data-mz-variant-id]') : null;
      if (hiddenId) hiddenId.value = variant.id;

      // Price + compare-at + "Save X"
      if (priceNowEl) priceNowEl.textContent = formatMoney(variant.price, moneyFormat);
      const hasDiscount = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
      if (priceWasEl) {
        priceWasEl.hidden = !hasDiscount;
        if (hasDiscount) priceWasEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
      }
      if (priceSaveEl) {
        priceSaveEl.hidden = !hasDiscount;
        if (hasDiscount) priceSaveEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price, moneyFormat);
      }

      // SKU
      if (skuEl) skuEl.textContent = variant.sku || 'MBZ';

      // Stock / button state
      [addBtn, buyBtnEl].forEach(btn => {
        if (!btn) return;
        btn.disabled = !variant.available;
      });
      if (addBtn) {
        const label = addBtn.querySelector('[data-mz-btn-label]');
        if (label) label.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      root.dispatchEvent(new CustomEvent('mz:variant-change', { detail: variant }));
    }

    // name: option name ("Color"/"Size"/...), value: the chosen value.
    // skipImageSwap: true when the gallery already swapped the photo
    // itself (e.g. a plain thumbnail click/swipe) and we only need to
    // sync the variant data, not swap the image again.
    function selectOptionValue(name, value, skipImageSwap) {
      const index = optionNames.indexOf(name);
      if (index === -1) return null;
      const nextOptions = selectedOptions.slice();
      nextOptions[index] = value;
      const variant = findVariantByOptions(nextOptions);
      if (!variant) return null; // combination doesn't exist as a real variant

      selectedOptions = nextOptions;
      if (!skipImageSwap) setVariantImage(variant);
      applyVariantToUI(variant);
      return variant;
    }

    const thumbs = Array.from(root.querySelectorAll('[data-mz-thumb]'));
    let activeIndex = thumbs.findIndex(t => t.getAttribute('aria-current') === 'true');
    if (activeIndex < 0) activeIndex = 0;

    // Lightweight preloading so thumbnail/swipe navigation feels instant.
    thumbs.forEach(t => {
      const full = t.getAttribute('data-full');
      if (full) { const img = new Image(); img.src = full; }
    });

    // The main image ships with a srcset/sizes pair for a fast, correctly
    // sized first paint (LCP). Once JS needs to swap it to a different
    // photo (gallery nav or a colour swatch), the srcset has to be
    // dropped first — otherwise the browser keeps resolving the old
    // srcset candidates and the visible photo never actually changes.
    function swapMainImage(url) {
      if (!mainImg || !url) return;
      if (mainImg.hasAttribute('srcset')) {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
      }
      mainImg.src = url;
    }

    /* ------------------------------------------------------------------
     * GALLERY — swap main image + keep dots/thumbs in sync
     * (navigation is swipe/drag + thumbnail click only)
     * ------------------------------------------------------------------ */
    function goToIndex(index) {
      if (!thumbs.length) return;
      const total = thumbs.length;
      activeIndex = ((index % total) + total) % total;
      const thumb = thumbs[activeIndex];
      const full = thumb.getAttribute('data-full');

      thumbs.forEach(t => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');

      if (mainImg && full) {
        mainImg.classList.add('is-swapping');
        window.setTimeout(() => {
          swapMainImage(full);
          mainImg.classList.remove('is-swapping');
        }, 90);
      }

      if (dotsWrap) {
        const dots = dotsWrap.querySelectorAll('.mz-gallery__dot');
        dots.forEach((d, i) => d.classList.toggle('is-active', i === activeIndex));
      }

      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

      // NEW: if this thumbnail is tagged with the colour variant it
      // belongs to (data-mz-color-value, set server-side), resolve and
      // apply that variant now — this is what makes browsing the
      // gallery (click OR swipe) actually change the selected variant,
      // not just the picture.
      const colorValue = thumb.getAttribute('data-mz-color-value');
      if (colorValue) {
        selectOptionValue('Color', colorValue, true) || selectOptionValue('Colour', colorValue, true);
        syncSwatchVisuals('Color', colorValue);
        syncSwatchVisuals('Colour', colorValue);
      }
    }

    // Keeps the swatch buttons' visual state (active ring + label text)
    // lined up when the *gallery* drives a colour change, since that
    // path doesn't go through the swatch click handler below.
    function syncSwatchVisuals(name, value) {
      const swatch = root.querySelector('.mz-swatch[data-mz-option-name="' + cssEscape(name) + '"][data-mz-option-value="' + cssEscape(value) + '"]');
      if (!swatch) return;
      const group = swatch.closest('.mz-swatches');
      if (group) {
        group.querySelectorAll('[data-mz-option-value]').forEach(b => {
          b.setAttribute('aria-current', 'false');
          b.setAttribute('aria-checked', 'false');
        });
      }
      swatch.setAttribute('aria-current', 'true');
      swatch.setAttribute('aria-checked', 'true');
      const label = root.querySelector('[data-mz-option-value="' + cssEscape(name) + '"]');
      if (label && !label.matches('button')) label.textContent = value;
    }

    function cssEscape(value) {
      return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');
    }

    root.addEventListener('click', function (e) {
      // ---- Thumbnail click ----
      const thumb = e.target.closest('[data-mz-thumb]');
      if (thumb) {
        goToIndex(thumbs.indexOf(thumb));
        return;
      }

      // ---- Option / swatch select ----
      const opt = e.target.closest('[data-mz-option-value]');
      if (opt && opt.tagName === 'BUTTON') {
        const group = opt.closest('.mz-swatches');
        if (group) group.querySelectorAll('[data-mz-option-value]').forEach(b => {
          b.setAttribute('aria-current', 'false');
          b.setAttribute('aria-checked', 'false');
        });
        opt.setAttribute('aria-current', 'true');
        opt.setAttribute('aria-checked', 'true');

        const label = root.querySelector('[data-mz-option-value="' + opt.dataset.mzOptionName + '"]');
        if (label && !label.matches('button')) label.textContent = opt.dataset.mzOptionValue;

        opt.classList.remove('mz-pop');
        void opt.offsetWidth; // restart animation
        opt.classList.add('mz-pop');

        // NEW: this now actually resolves and applies the matching
        // variant (id, price, SKU, stock state), instead of only
        // swapping the hero photo and leaving the old variant selected.
        const resolved = selectOptionValue(opt.dataset.mzOptionName, opt.dataset.mzOptionValue, false);

        // Fallback: if for some reason no variant matched (shouldn't
        // normally happen), at least keep the old "swap the photo for
        // this swatch" behaviour so the UI doesn't look broken.
        if (!resolved) {
          const swatchImage = opt.getAttribute('data-mz-swatch-image');
          if (swatchImage && mainImg) {
            mainImg.classList.add('is-swapping');
            window.setTimeout(() => {
              swapMainImage(swatchImage);
              mainImg.classList.remove('is-swapping');
            }, 90);
          }
        }

        root.dispatchEvent(new CustomEvent('mz:option-change', {
          detail: { name: opt.dataset.mzOptionName, value: opt.dataset.mzOptionValue }
        }));
        return;
      }

      // ---- Quantity ----
      if (e.target.closest('[data-mz-qty-minus]') && qtyInput) {
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        return;
      }
      if (e.target.closest('[data-mz-qty-plus]') && qtyInput) {
        const next = (parseInt(qtyInput.value, 10) || 1) + 1;
        qtyInput.value = Math.min(MAX_PER_PRODUCT, next);
        return;
      }

      // ---- Wishlist toggle ----
      const wishBtn = e.target.closest('[data-mz-wishlist]');
      if (wishBtn) {
        const pressed = wishBtn.getAttribute('aria-pressed') === 'true';
        const nowPressed = !pressed;
        wishBtn.setAttribute('aria-pressed', String(nowPressed));
        wishBtn.classList.remove('mz-pop');
        void wishBtn.offsetWidth;
        wishBtn.classList.add('mz-pop');

        if (nowPressed) {
          addToWishlist(wishBtn);
        } else {
          removeFromWishlist(wishBtn.getAttribute('data-mz-wishlist-handle') || wishBtn.getAttribute('data-handle'));
        }
        return;
      }

      // ---- Add-on toggle ----
      const addon = e.target.closest('[data-mz-addon]');
      if (addon) {
        const checked = addon.getAttribute('data-checked') === 'true';
        addon.setAttribute('data-checked', String(!checked));
        const cb = addon.querySelector('input[type="checkbox"]');
        if (cb) cb.checked = !checked;
        return;
      }

      // ---- Buy it now ----
      const buyBtn = e.target.closest('[data-mz-buy-now]');
      if (buyBtn) {
        handleBuyNow(buyBtn);
        return;
      }

      // ---- Sticky CTA add ----
      if (e.target.closest('[data-mz-sticky-add]')) {
        if (addBtn) addBtn.click();
        return;
      }
    });

    /* ------------------------------------------------------------------
     * CART CAP — never let this product's total quantity in the cart
     * exceed MAX_PER_PRODUCT, no matter how many separate times "Add to
     * Cart" / "Buy It Now" is pressed across the session.
     * ------------------------------------------------------------------ */
    function getCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
    }

    function getRemainingAllowed(variantId) {
      return getCart().then(cart => {
        const existing = (cart.items || [])
          .filter(item => String(item.variant_id) === String(variantId))
          .reduce((sum, item) => sum + item.quantity, 0);
        return Math.max(0, MAX_PER_PRODUCT - existing);
      });
    }

    function setCartMsg(text, state) {
      if (!cartMsgEl) return;
      if (!text) {
        cartMsgEl.hidden = true;
        cartMsgEl.textContent = '';
        cartMsgEl.removeAttribute('data-state');
        return;
      }
      cartMsgEl.hidden = false;
      cartMsgEl.textContent = text;
      cartMsgEl.setAttribute('data-state', state || 'error');
    }

    function updateCartBadge(cart) {
      // Generic hooks so this works whichever cart-icon markup the rest
      // of the theme uses — update whatever is present, skip the rest.
      document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach(el => {
        el.textContent = cart.item_count;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      document.dispatchEvent(new CustomEvent('mz:cart-updated', { detail: cart }));
    }

    function addToCart(items) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      }).then(r => {
        if (!r.ok) return r.json().then(err => Promise.reject(err));
        return r.json();
      });
    }

    function playAddedAnimation(btn) {
      const originalHtml = btn.innerHTML;
      btn.classList.add('mz-added');
      btn.innerHTML =
        '<svg class="mz-btn__check" viewBox="0 0 24 24"><polyline points="4 12 9 18 20 6"/></svg>' +
        '<span class="mz-btn__label">Added to Cart</span>';
      window.setTimeout(() => {
        btn.classList.remove('mz-added');
        btn.innerHTML = originalHtml;
      }, 1700);
    }

    if (productForm) {
      productForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!addBtn || addBtn.disabled || addBtn.classList.contains('is-loading')) return;

        const variantId = getVariantId();
        if (!variantId) return;

        const desiredQty = Math.max(1, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1);
        const checkedAddons = Array.from(root.querySelectorAll('[data-mz-addon][data-checked="true"] input[type="checkbox"]'))
          .map(cb => ({ id: cb.value, quantity: 1 }));

        addBtn.classList.add('is-loading');
        pressAnimate(addBtn);
        setCartMsg('');

        getRemainingAllowed(variantId).then(allowed => {
          if (allowed <= 0) {
            addBtn.classList.remove('is-loading');
            setCartMsg('You already have the maximum of ' + MAX_PER_PRODUCT + ' of this item in your cart.', 'error');
            return;
          }

          const finalQty = Math.min(desiredQty, allowed);
          const items = [{ id: variantId, quantity: finalQty }].concat(checkedAddons);

          addToCart(items).then(() => getCart()).then(cart => {
            addBtn.classList.remove('is-loading');
            updateCartBadge(cart);
            playAddedAnimation(addBtn);

            if (finalQty < desiredQty) {
              setCartMsg('Only ' + finalQty + ' added — max ' + MAX_PER_PRODUCT + ' of this item per cart.', 'success');
            } else {
              setCartMsg('Added to your cart.', 'success');
            }
          }).catch(() => {
            addBtn.classList.remove('is-loading');
            setCartMsg('Could not add to cart, please try again.', 'error');
          });
        });
      });
    }

    function handleBuyNow(buyBtn) {
      if (buyBtn.disabled) return;
      const variantId = getVariantId();
      if (!variantId) return;

      const desiredQty = Math.max(1, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1);
      pressAnimate(buyBtn);
      setCartMsg('');

      getRemainingAllowed(variantId).then(allowed => {
        if (allowed <= 0) {
          setCartMsg('You already have the maximum of ' + MAX_PER_PRODUCT + ' of this item in your cart.', 'error');
          return;
        }
        const finalQty = Math.min(desiredQty, allowed);
        window.setTimeout(() => {
          window.location.href = '/cart/' + variantId + ':' + finalQty;
        }, 180);
      });
    }

    function pressAnimate(btn) {
      btn.classList.remove('is-pressed');
      void btn.offsetWidth;
      btn.classList.add('is-pressed');
      window.setTimeout(() => btn.classList.remove('is-pressed'), 400);
    }

    /* ------------------------------------------------------------------
     * GALLERY — swipe (touch) + drag (cursor) support on the main image
     * ------------------------------------------------------------------ */
    function addSwipeNav(el, onPrev, onNext) {
      if (!el) return;
      let startX = 0, startY = 0, dragging = false, moved = false;

      el.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true; moved = false;
        startX = e.clientX; startY = e.clientY;
      }, { passive: true });
      el.addEventListener('pointermove', e => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved = true;
      }, { passive: true });
      const endDrag = e => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        const dx = (e.clientX || 0) - startX;
        const dy = (e.clientY || 0) - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) onNext(); else onPrev();
        }
      };
      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', () => { dragging = false; });
    }
    addSwipeNav(swipeArea, () => goToIndex(activeIndex - 1), () => goToIndex(activeIndex + 1));

    // Drag-to-scroll the (desktop-only) thumbnail rail with a mouse cursor.
    function addDragScroll(el) {
      if (!el) return;
      let isDown = false, startX = 0, startY = 0, scrollStart = 0;

      el.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse') return;
        isDown = true;
        el.classList.add('is-dragging');
        startX = e.clientX; startY = e.clientY;
        scrollStart = el.scrollTop;
      });
      el.addEventListener('pointermove', e => {
        if (!isDown) return;
        const delta = startY - e.clientY;
        el.scrollTop = scrollStart + delta;
      }, { passive: true });
      const stop = () => { isDown = false; el.classList.remove('is-dragging'); };
      el.addEventListener('pointerup', stop);
      el.addEventListener('pointerleave', stop);
      el.addEventListener('pointercancel', stop);
    }
    addDragScroll(thumbsStrip);

    /* ------------------------------------------------------------------
     * Sticky mobile CTA visibility, driven by IntersectionObserver
     * ------------------------------------------------------------------ */
    const buybox = root.querySelector('.mz-buybox');
    if (stickyCta && buybox && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        entries => entries.forEach(entry => stickyCta.classList.toggle('is-visible', !entry.isIntersecting)),
        { rootMargin: '0px 0px -60% 0px' }
      );
      io.observe(buybox);
    }

    /* ------------------------------------------------------------------
     * Live viewer counter — random 1..10, changes every few seconds
     * ------------------------------------------------------------------ */
    if (viewerCountEl) {
      let lastValue = parseInt(viewerCountEl.textContent, 10) || 6;

      function randomViewers(prev) {
        let next = prev;
        while (next === prev) next = Math.floor(Math.random() * 10) + 1;
        return next;
      }

      function tick() {
        const next = randomViewers(lastValue);
        lastValue = next;
        viewerCountEl.classList.add('is-updating');
        window.setTimeout(() => {
          viewerCountEl.textContent = next;
          viewerCountEl.classList.remove('is-updating');
        }, 250);
      }

      window.setTimeout(tick, 1200);
      window.setInterval(tick, 4000 + Math.random() * 3000);
    }

    /* ------------------------------------------------------------------
     * Description clean-up — rebuild ANY supplier "feature" block
     * (a <table>, or a <ul> laid out as flex/grid columns) into a single
     * vertical bullet list — bold lead phrase + rest of the text inline,
     * matching a plain competitor-style layout (no card, no icon badge).
     * ------------------------------------------------------------------ */
    const descRoot = root.querySelector('[data-mz-description]');
    if (descRoot) {
      const candidates = Array.from(descRoot.querySelectorAll('table, ul'));

      candidates.forEach(container => {
        const cellSelector = container.tagName === 'TABLE' ? 'td, th' : ':scope > li';
        const cells = Array.from(container.querySelectorAll(cellSelector)).filter(c => c.textContent.trim().length);
        if (cells.length < 2) return; // not a feature block, leave it alone

        const list = document.createElement('div');
        list.className = 'mz-feature-list';

        cells.forEach(cell => {
          let title = '';
          let desc = '';
          const strongEl = cell.querySelector('strong, b');

          if (strongEl) {
            title = strongEl.textContent.replace(/:\s*$/, '').trim();
            desc = cell.textContent.replace(strongEl.textContent, '').replace(/^:\s*/, '').trim();
          } else {
            const text = cell.textContent.trim();
            const idx = text.indexOf(':');
            if (idx > -1 && idx < 40) {
              title = text.slice(0, idx).trim();
              desc = text.slice(idx + 1).trim();
            } else {
              desc = text;
            }
          }

          const row = document.createElement('div');
          row.className = 'mz-feature-row';

          const bullet = document.createElement('span');
          bullet.className = 'mz-feature-row__bullet';

          const textEl = document.createElement('span');
          textEl.className = 'mz-feature-row__text';
          if (title) {
            const strongOut = document.createElement('strong');
            strongOut.textContent = title;
            textEl.appendChild(strongOut);
          }
          if (desc) {
            textEl.appendChild(document.createTextNode(desc));
          }

          row.appendChild(bullet);
          row.appendChild(textEl);
          list.appendChild(row);
        });

        container.replaceWith(list);
      });
    }
  }
})();